import { useCallback, useEffect, useMemo, useState } from "react"
import type { AppProps } from "next/app"
import Head from "next/head"
import { useRouter } from "next/router"

import { Box, ChakraProvider } from "@chakra-ui/react"

import { NuqsAdapter } from "nuqs/adapters/next/pages"
import { ApolloProvider } from "@apollo/client"
import {
  createTheme,
  type MantineColorsTuple,
  MantineProvider,
} from "@mantine/core"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

import { LoadingAnimation } from "@supliful/common"
import { suplifulTheme, theme } from "@supliful/theme"

import { AppReflagProvider } from "components/AppReflagProvider"
import { AuthProvider } from "components/AuthProvider"
import { useApollo } from "lib/apollo/client"
import { defaultChakraToastOptions } from "lib/config"
import { handleLogout } from "lib/hooks/useLogout"
import { getQueryClient, TRPCProvider, useTrpcClient } from "lib/trpc"

import "@mantine/core/styles.css"

type RouterOptions = { shallow?: boolean }

const primary: MantineColorsTuple = [
  suplifulTheme.colors.primary[25],
  suplifulTheme.colors.primary[50],
  suplifulTheme.colors.primary[100],
  suplifulTheme.colors.primary[200],
  suplifulTheme.colors.primary[300],
  suplifulTheme.colors.primary[400],
  suplifulTheme.colors.primary[500],
  suplifulTheme.colors.primary[600],
  suplifulTheme.colors.primary[700],
  suplifulTheme.colors.primary[900],
]

const neutrals: MantineColorsTuple = [
  suplifulTheme.colors.neutrals[20],
  suplifulTheme.colors.neutrals[50],
  suplifulTheme.colors.neutrals[100],
  suplifulTheme.colors.neutrals[200],
  suplifulTheme.colors.neutrals[300],
  suplifulTheme.colors.neutrals[400],
  suplifulTheme.colors.neutrals[500],
  suplifulTheme.colors.neutrals[600],
  suplifulTheme.colors.neutrals[700],
  suplifulTheme.colors.neutrals[900],
]

const mantineTheme = createTheme({
  colors: {
    primary,
    neutrals,
  },
  cursorType: "pointer",
  defaultRadius: "md",
  fontFamily: suplifulTheme.fonts.body,
  headings: {
    fontFamily: suplifulTheme.fonts.heading,
  },
  primaryColor: "primary",
})

export default function AdminApp(props: AppProps<{ initialApolloState: any }>) {
  const { Component, pageProps } = props
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const apolloClient = useApollo(pageProps.initialApolloState)
  const logout = useCallback(
    () => handleLogout({ router, apolloClient, queryClient: getQueryClient() }),
    [router, apolloClient],
  )
  const queryClient = useMemo(() => getQueryClient(logout), [logout])
  const trpcClient = useTrpcClient()

  useEffect(() => {
    const routeChangeStart = (url: string, { shallow }: RouterOptions) => {
      if (!shallow) {
        setIsLoading(true)
      }
    }
    const routeChangeComplete = () => {
      setIsLoading(false)
    }
    const routeChangeError = () => {
      setIsLoading(false)
    }

    router.events.on("routeChangeStart", routeChangeStart)
    router.events.on("routeChangeComplete", routeChangeComplete)
    router.events.on("routeChangeError", routeChangeError)

    return () => {
      router.events.off("routeChangeStart", routeChangeStart)
      router.events.off("routeChangeComplete", routeChangeComplete)
      router.events.off("routeChangeError", routeChangeError)
    }
  }, [router.events])

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>

      <ChakraProvider
        theme={theme}
        toastOptions={{ defaultOptions: defaultChakraToastOptions }}
      >
        <MantineProvider theme={mantineTheme}>
          <QueryClientProvider client={queryClient}>
            <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
              <ApolloProvider client={apolloClient}>
                <AppReflagProvider>
                  <NuqsAdapter>
                    <AuthProvider>
                      {isLoading && (
                        <Box
                          position="fixed"
                          top={0}
                          bottom={0}
                          left={0}
                          right={0}
                          bg="whiteAlpha.600"
                          zIndex={200}
                        >
                          <LoadingAnimation
                            wrapperProps={{
                              position: "absolute",
                              top: "20vh",
                              left: "50%",
                              transform: "translateX(-50%)",
                            }}
                          />
                        </Box>
                      )}

                      <Component {...pageProps} />
                    </AuthProvider>
                  </NuqsAdapter>
                </AppReflagProvider>
              </ApolloProvider>
            </TRPCProvider>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </MantineProvider>
      </ChakraProvider>
    </>
  )
}
